import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGameStore } from '../stores/gameStore'
import { useSocket } from '../context/SocketContext'

const FallenPage = () => {
  const navigate = useNavigate()
  const [showResults, setShowResults] = useState(true)
  
  const {
    player,
    gameState,
    lastMatchResult,
    matchRequestData,
    error,
    clearError,
    setGameState,
    resetGame,
  } = useGameStore()
  
  const { requestTryAgain, respondToTryAgain, joinLobby, notifyPartnerLeft, requestChat, respondToChatRequest } = useSocket()

  useEffect(() => {
    // Debug: Log the lastMatchResult data
    if (lastMatchResult) {
      console.log('FallenPage - lastMatchResult:', lastMatchResult)
      console.log('FallenPage - game_summary:', lastMatchResult.game_summary)
    }
    
    // Redirect if not in fallen or try again or chat request states
    if (gameState !== 'fallen' && gameState !== 'try_again_request_sent' && gameState !== 'try_again_request_received' && 
        gameState !== 'chat_request_sent' && gameState !== 'chat_request_received' && gameState !== 'chat') {
      navigate('/lobby')
    }
  }, [gameState, navigate, lastMatchResult])

  useEffect(() => {
    // Handle game state changes - navigate when game starts
    if (gameState === 'in_game') {
      navigate('/game')
    }
  }, [gameState, navigate])

  useEffect(() => {
    // Handle chat state - navigate to chat page
    if (gameState === 'chat' && matchRequestData && matchRequestData.chat_id) {
      navigate(`/chat/${matchRequestData.chat_id}`)
    }
  }, [gameState, matchRequestData, navigate])

  // Scroll to top when page loads or state changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [gameState])

  const handleTryAgain = () => {
    if (lastMatchResult && lastMatchResult.partner_user_id) {
      clearError()
      console.log('Requesting try again with partner:', lastMatchResult.partner_identity)
      requestTryAgain(lastMatchResult.partner_user_id)
    }
  }

  const handleGoToPurgatory = () => {
    // Notify partner that we're leaving (if we have partner info)
    if (lastMatchResult && lastMatchResult.partner_user_id) {
      notifyPartnerLeft(lastMatchResult.partner_user_id)
    }
    
    // Clear any lingering data and set state to lobby
    setGameState('lobby')
    clearError()
    
    // Navigate to lobby page
    navigate('/lobby')
  }

  const handleAcceptTryAgain = () => {
    if (matchRequestData && matchRequestData.requester_id) {
      respondToTryAgain(matchRequestData.requester_id, true)
    }
  }

  const handleDeclineTryAgain = () => {
    if (matchRequestData && matchRequestData.requester_id) {
      respondToTryAgain(matchRequestData.requester_id, false)
    }
  }

  const handleRequestChat = () => {
    // Request to chat with the partner from the last match
    if (lastMatchResult && lastMatchResult.partner_user_id) {
      clearError()
      requestChat(lastMatchResult.partner_user_id)
    } else {
      setError('Unable to request chat - partner information not available')
    }
  }

  const handleAcceptChatRequest = () => {
    if (matchRequestData && matchRequestData.requester_id) {
      respondToChatRequest(matchRequestData.requester_id, true)
    }
  }

  const handleDeclineChatRequest = () => {
    if (matchRequestData && matchRequestData.requester_id) {
      respondToChatRequest(matchRequestData.requester_id, false)
    }
  }

  if (!player.identity) {
    navigate('/')
    return null
  }

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-6xl font-bold text-purgatory-ember mb-4">
            FALLEN
          </h1>
          <p className="text-purgatory-mist font-mono">
            The ascent ends, but hope remains.
          </p>
        </div>

        {/* Error display */}
        {error && (
          <div className="bg-purgatory-ember bg-opacity-20 border border-purgatory-ember p-4 rounded mb-6">
            <p className="text-purgatory-ember text-sm font-mono">{error}</p>
          </div>
        )}

        {/* Try Again Request Sent State */}
        {gameState === 'try_again_request_sent' && matchRequestData && (
          <div className="max-w-2xl mx-auto">
            <div className="card-retro p-8 text-center">
              <div className="animate-pulse w-16 h-16 border-4 border-purgatory-ash border-t-neon-blue rounded-full mx-auto mb-6"></div>
              <h2 className="text-2xl font-bold text-neon-blue mb-4">
                Try Again Request Sent
              </h2>
              <div className="text-4xl mb-4">{matchRequestData.partner_identity}</div>
              <p className="text-purgatory-mist mb-6">
                {matchRequestData.message}
              </p>
              <button
                onClick={handleGoToPurgatory}
                className="btn-retro btn-danger"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Try Again Request Received State */}
        {gameState === 'try_again_request_received' && matchRequestData && (
          <div className="max-w-2xl mx-auto">
            <div className="card-retro p-8 text-center">
              <h2 className="text-2xl font-bold text-purgatory-gold mb-4">
                Try Again Request
              </h2>
              <div className="text-4xl mb-4">{matchRequestData.requester_identity}</div>
              <p className="text-xl text-purgatory-mist mb-8">
                {matchRequestData.message}
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={handleAcceptTryAgain}
                  className="btn-retro px-8 py-3 text-lg"
                >
                  YES
                </button>
                <button
                  onClick={handleDeclineTryAgain}
                  className="btn-retro btn-danger px-8 py-3 text-lg"
                >
                  NO
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Chat Request Sent State */}
        {gameState === 'chat_request_sent' && matchRequestData && (
          <div className="max-w-2xl mx-auto">
            <div className="card-retro p-8 text-center">
              <div className="animate-pulse w-16 h-16 border-4 border-purgatory-ash border-t-neon-blue rounded-full mx-auto mb-6"></div>
              <h2 className="text-2xl font-bold text-neon-blue mb-4">
                Chat Request Sent
              </h2>
              <p className="text-purgatory-mist mb-6">
                {matchRequestData.message}
              </p>
              <button
                onClick={handleGoToPurgatory}
                className="btn-retro btn-danger"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Chat Request Received State */}
        {gameState === 'chat_request_received' && matchRequestData && (
          <div className="max-w-2xl mx-auto">
            <div className="card-retro p-8 text-center">
              <h2 className="text-2xl font-bold text-neon-blue mb-4">
                Chat Request
              </h2>
              <div className="text-4xl mb-4">{matchRequestData.requester_identity}</div>
              <p className="text-xl text-purgatory-mist mb-8">
                wants to chat with you
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={handleAcceptChatRequest}
                  className="btn-retro px-8 py-3 text-lg"
                >
                  Accept
                </button>
                <button
                  onClick={handleDeclineChatRequest}
                  className="btn-retro btn-danger px-8 py-3 text-lg"
                >
                  Reject
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Fallen State - Progress Summary */}
        {gameState === 'fallen' && lastMatchResult && (
          <div className="max-w-2xl mx-auto">
            <div className="card-retro p-8 text-center">
              {/* Partnership Summary */}
              <div className="text-center mb-8">
                <div className="text-8xl mb-4">😔</div>
                <h1 className="text-4xl font-bold text-purgatory-ember mb-4">
                  PARTNERSHIP ENDED
                </h1>
                <p className="text-purgatory-mist text-lg mb-2">
                  Just because you are different doesn't mean you can't learn from each other.
                </p>
                <div className="text-6xl font-bold text-purgatory-ember mb-2">
                  {(() => {
                    const totalMatches = lastMatchResult.game_summary?.total_matches || lastMatchResult.total_matches || lastMatchResult.matches || 0;
                    const totalQuestions = lastMatchResult.game_summary?.total_questions || (lastMatchResult.rounds_completed || 1) * 3;
                    return Math.round((totalMatches / totalQuestions) * 100);
                  })()}%
                </div>
                <p className="text-purgatory-mist text-lg">Common Ground (Total)</p>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <button
                  onClick={handleRequestChat}
                  className="btn-primary text-xl py-6 bg-gradient-to-r from-neon-blue to-purple-600 text-white border-2 border-neon-blue hover:from-neon-blue hover:to-purple-700 shadow-lg transform hover:scale-105 transition-all"
                >
                  💬 CHAT
                </button>
                <button
                  onClick={handleGoToPurgatory}
                  className="btn-secondary text-xl py-6 border-2 border-purgatory-gold text-purgatory-gold hover:bg-purgatory-gold hover:text-purgatory-dark"
                >
                  🏠 RETURN
                </button>
              </div>

              {/* Round Summary - Collapsible Section */}
              <div className="card-retro p-6 mb-8">
                <button
                  onClick={() => setShowResults(!showResults)}
                  className="w-full text-left flex items-center justify-between"
                >
                  <h3 className="text-purgatory-gold font-bold text-lg">
                    Your Results
                  </h3>
                  <div className="text-purgatory-gold text-xl transform transition-transform duration-200">
                    {showResults ? '−' : '+'}
                  </div>
                </button>
                
                {showResults && (
                  <div className="mt-4 space-y-4">
                    {/* Round Summaries - Show all rounds */}
                    {lastMatchResult.game_summary && lastMatchResult.game_summary.rounds ? (
                      lastMatchResult.game_summary.rounds.map((round, roundIndex) => {
                        console.log(`Round ${roundIndex + 1} data:`, round);
                        return (
                        <div key={roundIndex} className="mb-6">
                          <div className="text-lg font-bold text-neon-green mb-3">
                            Round {roundIndex + 1} - {(() => {
                              const matches = round.matches_count || round.matches || 0;
                              const totalQuestions = round.questions ? round.questions.length : 3;
                              const percentage = Math.round((matches / totalQuestions) * 100);
                              console.log(`Round ${roundIndex + 1}: ${matches} matches out of ${totalQuestions} questions = ${percentage}%`);
                              return percentage;
                            })()}% Match
                          </div>
                          <div className="space-y-3">
                            {round.questions && round.questions.map((question, qIndex) => (
                              <div key={qIndex} className="bg-purgatory-ash bg-opacity-20 p-3 rounded">
                                <div className="text-sm text-purgatory-mist mb-2">
                                  Question {qIndex + 1}
                                </div>
                                <div className="text-sm mb-2">{question.text}</div>
                                <div className="grid grid-cols-2 gap-4 text-xs">
                                  <div>
                                    <span className="text-purgatory-mist">You: </span>
                                    <span className={`font-bold ${
                                      (round.match_details && round.match_details[qIndex]) || question.match ? 'text-neon-green' : 'text-purgatory-ember'
                                    }`}>
                                      {question.player1_answer || (question.options && round.user1_answers && question.options[round.user1_answers[qIndex]]) || 'No answer'}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-purgatory-mist">Them: </span>
                                    <span className={`font-bold ${
                                      (round.match_details && round.match_details[qIndex]) || question.match ? 'text-neon-green' : 'text-purgatory-ember'
                                    }`}>
                                      {question.player2_answer || (question.options && round.user2_answers && question.options[round.user2_answers[qIndex]]) || 'No answer'}
                                    </span>
                                  </div>
                                </div>
                                <div className="text-center mt-2">
                                  {(round.match_details && round.match_details[qIndex]) || question.match ? (
                                    <span className="text-neon-green text-xs">✓ MATCH</span>
                                  ) : (
                                    <span className="text-purgatory-ember text-xs">✗ NO MATCH</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        );
                      })
                    ) : (
                      /* Fallback - show all rounds from stored data */
                      <div className="space-y-6">
                        {Array.from({ length: lastMatchResult.rounds_completed || 1 }, (_, roundIndex) => (
                          <div key={roundIndex} className="mb-6">
                            <div className="text-lg font-bold text-neon-green mb-3">
                              Round {roundIndex + 1} - {roundIndex === (lastMatchResult.rounds_completed || 1) - 1 
                                ? Math.round(((lastMatchResult.matches || 0) / 3) * 100) 
                                : '100'}% Match
                            </div>
                            <div className="space-y-3">
                              {(lastMatchResult.questions || []).map((question, qIndex) => (
                                <div key={qIndex} className="bg-purgatory-ash bg-opacity-20 p-3 rounded">
                                  <div className="text-sm text-purgatory-mist mb-2">
                                    Question {qIndex + 1}
                                  </div>
                                  <div className="text-sm mb-2">{question.text}</div>
                                  <div className="grid grid-cols-2 gap-4 text-xs">
                                    <div>
                                      <span className="text-purgatory-mist">You: </span>
                                      <span className={`font-bold ${
                                        (lastMatchResult.match_details && lastMatchResult.match_details[qIndex]) ? 'text-neon-green' : 'text-purgatory-ember'
                                      }`}>
                                        {(lastMatchResult.player1_answers && question.options && question.options[lastMatchResult.player1_answers[qIndex]]) || 'No answer'}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-purgatory-mist">Them: </span>
                                      <span className={`font-bold ${
                                        (lastMatchResult.match_details && lastMatchResult.match_details[qIndex]) ? 'text-neon-green' : 'text-purgatory-ember'
                                      }`}>
                                        {(lastMatchResult.player2_answers && question.options && question.options[lastMatchResult.player2_answers[qIndex]]) || 'No answer'}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="text-center mt-2">
                                    {(lastMatchResult.match_details && lastMatchResult.match_details[qIndex]) ? (
                                      <span className="text-neon-green text-xs">✓ MATCH</span>
                                    ) : (
                                      <span className="text-purgatory-ember text-xs">✗ NO MATCH</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Donate Link */}
              <div className="text-center mt-8">
                <a 
                  href="https://checkout.revolut.com/pay/4b87e61e-9c30-43a3-8822-9877bed4fe24" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purgatory-mist text-sm hover:text-purgatory-gold transition-colors underline"
                >
                  Donate to support our work
                </a>
              </div>
            </div>
          </div>
        )}


      </div>
    </div>
  )
}

export default FallenPage
